import { useState, useEffect } from 'react';
import { t } from 'i18n';
import { Link } from 'react-router-dom';
import api from 'services/api';
// ⚠️ DRAPEAUX EN SVG, PAS EN EMOJI UNICODE. Windows ne porte pas les emojis
//    drapeaux : il affiche les deux lettres du code (« TG ») a la place. Le
//    paquet `flag-icons` embarque les SVG — rendu identique partout, et
//    HORS-LIGNE : aucune requete reseau, rien a charger depuis un CDN.
import 'flag-icons/css/flag-icons.min.css';

/* ═══════════════════════════════════════════════════════════════════════════
 * REFERENTIEL PAYS — l'ecran qui manquait
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ Depuis le 02/09/2026, le message d'erreur de creation d'un tenant
 *    renvoyait vers /super-admin/referentiel-pays — une page qui n'existait
 *    pas. Les 17 pays vides ne se renseignaient qu'en SQL.
 *
 * ⚠️ CET ECRAN N'INVENTE AUCUNE VALEUR. Il affiche ce que porte la base, dit ce
 *    qui manque, et transmet ce que l'utilisateur saisit. Les regles (source
 *    obligatoire avec un taux, taux plausible, refus de suppression d'un pays
 *    utilise) sont portees par la BASE, pas par ce fichier.
 */

function saApi() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('gss_sa_token')}` } };
}

/**
 * Le drapeau d'un pays, derive de son code ISO alpha-2.
 * ⚠️ UN CODE SANS DRAPEAU N'AFFICHE RIEN — pas d'icone de remplacement. Un
 *    point d'interrogation ou un carre gris ferait croire a une donnee
 *    manquante alors que c'est l'illustration qui manque, pas le pays.
 */
function Drapeau({ code }) {
  if (!code || String(code).length !== 2) return null;
  return (
    <span
      className={`fi fi-${String(code).toLowerCase()}`}
      // Aligne sur la hauteur du texte : le drapeau accompagne le nom, il ne
      // le domine pas.
      style={{ width: '1.25em', height: '0.9em', marginRight: 8,
        verticalAlign: 'baseline', display: 'inline-block', borderRadius: 2,
        boxShadow: '0 0 0 1px rgba(0,0,0,0.12)' }}
      aria-hidden="true"
    />
  );
}

const ETAT_STYLE = {
  complet: { fond: 'rgba(34,197,94,0.12)', texte: 'var(--gss-success)', libelle: 'COMPLET' },
  partiel: { fond: 'rgba(234,179,8,0.14)', texte: 'var(--gss-warning)', libelle: 'PARTIEL' },
  vide:    { fond: 'rgba(148,163,184,0.14)', texte: 'var(--gss-text-light)', libelle: 'VIDE' },
};

const CHAMPS = [
  { cle: 'libelle', label: 'Libellé du pays' },
  { cle: 'devise', label: 'Devise (ISO 4217)', placeholder: 'XOF, XAF, NGN...' },
  { cle: 'zone', label: 'Zone monétaire / juridique', placeholder: 'UEMOA, CEMAC, OHADA...' },
  { cle: 'taux_tva', label: 'Taux de TVA (%)', placeholder: '18', aide: 'Laisser vide tant que le taux n\'est pas établi. Un taux exige sa source.' },
  { cle: 'libelle_identifiant_commerce', label: 'Identifiant du commerce', placeholder: 'RCCM', aide: 'Le nom LOCAL : RCCM au Togo, RCCM au Cameroun...' },
  { cle: 'libelle_identifiant_fiscal', label: 'Identifiant fiscal', placeholder: 'NIF, NIU, IFU...' },
  { cle: 'libelle_identifiant_social', label: 'Identifiant social', placeholder: 'CNSS, CNPS...' },
];

export default function SuperAdminReferentielPays() {
  const [pays, setPays] = useState([]);
  const [resume, setResume] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [fiche, setFiche] = useState(null);
  const [form, setForm] = useState({});
  const [histo, setHisto] = useState([]);
  const [erreur, setErreur] = useState(null);
  const [message, setMessage] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = () => {
    setChargement(true);
    api.get('/super-admin/referentiel-pays', saApi())
      // ⚠️ ORDRE ALPHABETIQUE STRICT (Dom, 03/09/2026). Le backend triait par
      //    `membre_ohada DESC` d'abord, ce qui rejetait le Ghana et le Nigeria
      //    en fin de liste. L'APPARTENANCE OHADA EST UNE ETIQUETTE, PAS UN RANG.
      //    `localeCompare('fr')` place « Bénin » avant « Burkina » et ignore les
      //    accents comme le ferait un lecteur francophone.
      .then(r => {
        const liste = (r.data.pays || []).slice()
          .sort((a, b) => String(a.libelle || '').localeCompare(String(b.libelle || ''), 'fr'));
        setPays(liste);
        setResume(r.data.resume || null);
      })
      .catch(() => setErreur("Le référentiel pays n'a pas pu être lu."))
      .finally(() => setChargement(false));
  };
  useEffect(charger, []);

  const ouvrir = (p) => {
    setErreur(null); setMessage(null);
    setFiche(p);
    setForm({
      libelle: p.libelle || '', devise: p.devise || '', zone: p.zone || '',
      taux_tva: p.taux_tva === null || p.taux_tva === undefined ? '' : String(p.taux_tva),
      libelle_identifiant_commerce: p.libelle_identifiant_commerce || '',
      libelle_identifiant_fiscal: p.libelle_identifiant_fiscal || '',
      libelle_identifiant_social: p.libelle_identifiant_social || '',
      source_validation: p.source_validation || '',
      note_interne: p.note_interne || '',
      actif: p.actif,
    });
    setHisto([]);
    api.get(`/super-admin/referentiel-pays/${p.pays_code}/historique`, saApi())
      .then(r => setHisto(r.data.modifications || []))
      .catch(() => setHisto([]));
  };

  // ⚠️ LA SOURCE DEVIENT OBLIGATOIRE DES QU'UN TAUX EST SAISI. Le garde est ici
  //    pour que l'utilisateur le voie AVANT d'envoyer ; la contrainte en base
  //    reste l'autorite (elle vaut aussi pour une modification en SQL direct).
  const tauxSaisi = String(form.taux_tva || '').trim() !== '';
  const sourceManquante = tauxSaisi && String(form.source_validation || '').trim() === '';

  // ⚠️ LA LISTE DES MANQUES VIENT DU SERVEUR, jamais recalculee ici. Elle est
  //    produite par la fonction de base `referentiel_pays_manques`, celle-la
  //    MEME qui refuse l'ecriture. Recalculer cote ecran, ce serait deux listes
  //    en sursis : un ecran qui dit « complet » sur un pays que le garde refuse.
  const manques = (fiche && fiche.manques) || [];
  const referentielIncomplet = manques.length > 0;
  /* ⓘ LA COUVERTURE EST UN ETAT A PART, PAS UNE LIGNE DE `manques`. Un pays peut
   *   etre COMPLET — donc ouvrable, donc facturable — et ne pas savoir porter ses
   *   emplois de maitrise ou d encadrement. Les melanger fermerait le Togo. */
  const couverture = (fiche && fiche.couverture) || null;

  const enregistrer = async () => {
    setErreur(null); setMessage(null); setEnvoi(true);
    try {
      const r = await api.put(`/super-admin/referentiel-pays/${fiche.pays_code}`, {
        ...form,
        taux_tva: tauxSaisi ? form.taux_tva : null,
      }, saApi());
      setMessage(`${r.data.pays.libelle} enregistre.`);
      charger();
      ouvrir(r.data.pays);
    } catch (e) {
      setErreur(e?.response?.data?.error || "L'enregistrement a échoué.");
    } finally { setEnvoi(false); }
  };

  const S = {
    carte: { background: 'var(--gss-surface)', border: '1px solid var(--gss-border)', borderRadius: 10, padding: 16 },
    th: { textAlign: 'left', padding: '10px 12px', fontSize: 11, textTransform: 'uppercase',
      color: 'var(--gss-text-light)', borderBottom: '1px solid var(--gss-border)' },
    td: { padding: '10px 12px', borderBottom: '1px solid var(--gss-border)', fontSize: 13 },
    label: { display: 'block', fontSize: 12, color: 'var(--gss-text-light)', marginBottom: 4 },
    input: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gss-border)',
      background: 'var(--gss-bg)', color: 'var(--gss-text)', fontSize: 13 },
    aide: { fontSize: 11, color: 'var(--gss-text-light)', marginTop: 3 },
  };

  return (
    <div style={{ padding: 20 }}>
      {/* ⚠️ LE FIL D'ARIANE — motif repris des autres ecrans super-admin. Le menu
          n'existe QUE sur le tableau de bord : sans ce lien, cette page etait un
          cul-de-sac, on n'en sortait qu'avec le bouton du navigateur. */}
      <div style={{ marginBottom: 20 }}>
        <Link to="/super-admin" style={{ color: 'var(--gss-text-light)', fontSize: 12, textDecoration: 'none' }}>Dashboard</Link>
        <span style={{ color: 'var(--gss-text-light)', margin: '0 8px' }}>/</span>
        <span style={{ color: 'var(--gss-text)', fontSize: 14, fontWeight: 700 }}>Référentiel pays</span>
      </div>
      <h1 style={{ margin: '0 0 4px', fontSize: 20 }}>Référentiel pays</h1>
      <p style={{ margin: '0 0 16px', color: 'var(--gss-text-light)', fontSize: 13 }}>
        Un pays doit etre renseigne <strong>avant</strong> qu'un client puisse y etre cree.
        Le systeme ne devine jamais une devise ni un taux de TVA.
      </p>

      {/* ⚠️ LE COMPTE DIT L'ETAT REEL : « 2 complets sur 19 » ouvre une action. */}
      {resume && (
        <div style={{ ...S.carte, marginBottom: 16, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div><div style={S.label}>{t('Pays')}</div><strong style={{ fontSize: 18 }}>{resume.total}</strong>
            <span style={{ color: 'var(--gss-text-light)', fontSize: 12 }}> {t('dont {n} membres OHADA', { n: resume.ohada })}</span></div>
          {/* ⚠️ DEUX QUESTIONS, DEUX CHIFFRES. « Membre OHADA » est un FAIT
              juridique ; « servi » est ce que LE PRODUIT couvre. Le Mali est
              membre et n'a jamais ete parametre. Les confondre ferait annoncer
              17 pays servis a un prospect — un chiffre faux devant un prospect
              coute la credibilite de tout le reste. */}
          <div><div style={S.label}>{t('Servis')}</div>
            <strong style={{ fontSize: 18, color: 'var(--gss-success)' }}>{resume.servi}</strong>
            <span style={{ color: 'var(--gss-text-light)', fontSize: 12 }}> {t('paramétrés et éprouvés')}</span></div>
          <div><div style={S.label}>{t('Cadre posé')}</div>
            <strong style={{ fontSize: 18, color: 'var(--gss-warning)' }}>{resume.cadre_pose}</strong>
            <span style={{ color: 'var(--gss-text-light)', fontSize: 12 }}> {t('paramétrage à faire')}</span></div>
          <div><div style={S.label}>{t('Hors périmètre')}</div>
            <strong style={{ fontSize: 18, color: 'var(--gss-text-light)' }}>{resume.hors_perimetre}</strong>
            <span style={{ color: 'var(--gss-text-light)', fontSize: 12 }}> {t('non proposés à la création')}</span></div>
          <div><div style={S.label}>Complets</div><strong style={{ fontSize: 18, color: 'var(--gss-success)' }}>{resume.complets}</strong></div>
          <div><div style={S.label}>Partiels</div><strong style={{ fontSize: 18, color: 'var(--gss-warning)' }}>{resume.partiels}</strong></div>
          <div><div style={S.label}>Vides</div><strong style={{ fontSize: 18, color: 'var(--gss-text-light)' }}>{resume.vides}</strong></div>
        </div>
      )}

      {chargement && <p style={{ color: 'var(--gss-text-light)' }}>Lecture du référentiel…</p>}

      <div style={{ display: 'grid', gridTemplateColumns: fiche ? '1fr 1fr' : '1fr', gap: 16, alignItems: 'start' }}>
        <div style={{ ...S.carte, padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              {['Pays', 'Devise', 'TVA', 'Etat', 'Clients', ''].map(h => <th key={h} style={S.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {pays.map(p => {
                const st = ETAT_STYLE[p.etat] || ETAT_STYLE.vide;
                return (
                  <tr key={p.pays_code} style={{ background: fiche?.pays_code === p.pays_code ? 'rgba(139,26,26,0.06)' : 'transparent' }}>
                    <td style={S.td}>
                      <Drapeau code={p.pays_code} /><strong>{p.libelle}</strong>
                      <span style={{ color: 'var(--gss-text-light)', fontSize: 11 }}> {p.pays_code}</span>
                      {!p.membre_ohada && <span style={{ fontSize: 10, color: 'var(--gss-text-light)' }}> · {t('hors OHADA')}</span>}
                      {/* ⚠️ LE NIVEAU DE COUVERTURE, SUR CHAQUE LIGNE. Sans lui,
                          rien ne distingue un pays eprouve d'un pays jamais
                          parametre — et l'ecran laisse croire qu'ils se valent. */}
                      {p.niveau_couverture && (
                        <span title={p.note_couverture || ''} style={{
                          marginLeft: 8, fontSize: 10, padding: '2px 7px', borderRadius: 5,
                          whiteSpace: 'nowrap',
                          background: p.niveau_couverture === 'servi' ? 'rgba(26,135,84,0.10)'
                            : p.niveau_couverture === 'cadre_pose' ? 'rgba(245,158,11,0.12)' : 'rgba(0,0,0,0.06)',
                          color: p.niveau_couverture === 'servi' ? '#1A8754'
                            : p.niveau_couverture === 'cadre_pose' ? '#92400E' : 'var(--gss-text-light)',
                        }}>
                          {p.niveau_couverture === 'servi' ? t('servi')
                            : p.niveau_couverture === 'cadre_pose' ? t('cadre posé') : t('hors périmètre')}
                        </span>
                      )}
                      {!p.actif && <span style={{ fontSize: 10, color: 'var(--gss-danger)' }}> · désactivé</span>}
                    </td>
                    <td style={S.td}>{p.devise}</td>
                    {/* ⚠️ « — » et non « 0 » : un taux absent n'est pas un taux nul. */}
                    <td style={S.td}>{p.taux_tva === null || p.taux_tva === undefined
                      ? <span style={{ color: 'var(--gss-text-light)' }}>—</span>
                      : `${Number(p.taux_tva).toLocaleString('fr-FR')} %`}</td>
                    <td style={S.td}>
                      <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                        background: st.fond, color: st.texte }}>{st.libelle}</span>
                    </td>
                    <td style={S.td}>{p.nb_tenants || 0}</td>
                    <td style={S.td}>
                      <button onClick={() => ouvrir(p)} style={{ padding: '4px 10px', fontSize: 12, cursor: 'pointer',
                        borderRadius: 6, border: '1px solid var(--gss-border)', background: 'transparent', color: 'var(--gss-text)' }}>
                        Ouvrir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {fiche && (
          <div style={S.carte}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ margin: 0, fontSize: 16 }}>
                <Drapeau code={fiche.pays_code} />{fiche.libelle}
                <span style={{ color: 'var(--gss-text-light)', fontSize: 12, marginLeft: 6 }}>{fiche.pays_code}</span>
              </h2>
              <button onClick={() => setFiche(null)} style={{ background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--gss-text-light)', fontSize: 18 }}>×</button>
            </div>

            {/* ⚠️ UN PAYS UTILISE NE SE SUPPRIME PAS — et on le DIT, plutot que
                d'afficher un bouton qui echouera. */}
            {fiche.nb_tenants > 0 && (
              <div style={{ background: 'rgba(234,179,8,0.12)', color: 'var(--gss-warning)', padding: '8px 10px',
                borderRadius: 6, fontSize: 12, marginBottom: 12 }}>
                Utilise par <strong>{fiche.nb_tenants} client(s)</strong> — ce pays ne peut pas etre supprime.
                Pour le retirer des choix, le desactiver.
              </div>
            )}

            {CHAMPS.map(c => (
              <div key={c.cle} style={{ marginBottom: 12 }}>
                <label style={S.label}>{c.label}</label>
                <input style={S.input} value={form[c.cle] ?? ''} placeholder={c.placeholder || ''}
                  onChange={e => setForm({ ...form, [c.cle]: e.target.value })} />
                {c.aide && <div style={S.aide}>{c.aide}</div>}
              </div>
            ))}

            {/* ⚠️ DEUX CHAMPS, JAMAIS UN. La source FONDE le taux ; une réserve
                est une DETTE. Mesuré le 11/09/2026 : les deux pays renseignés
                portaient « À FAIRE CONFIRMER PAR UN COMPTABLE » À L'INTÉRIEUR de
                leur source. Une dette rangée dans le champ qui fonde une valeur
                finit par être lue comme une justification. */}
            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>
                Source du taux de TVA {tauxSaisi && <strong style={{ color: 'var(--gss-danger)' }}>— obligatoire</strong>}
              </label>
              <textarea rows={3} style={{ ...S.input, resize: 'vertical' }} value={form.source_validation ?? ''}
                placeholder="Intitulé du texte, numéro, date. Exemple : « Code général des impôts, loi n° 2023-012 du 04/10/2023, article 42 »."
                onChange={e => setForm({ ...form, source_validation: e.target.value })} />
              <div style={S.aide}>
                Un taux sans source est un chiffre que personne ne pourra défendre devant l&apos;administration fiscale.
                Le texte doit porter son <strong>intitulé</strong>, son <strong>numéro</strong> et sa <strong>date</strong> —
                le serveur refuse une source de moins de 20 caractères, ou sans année.
                <br />
                <strong>Une réserve n&apos;est pas une source.</strong> « À confirmer », « à valider » sont refusés ici :
                ils vont dans la note interne ci-dessous.
                {fiche.date_validation && <> Dernière validation : <strong>{new Date(fiche.date_validation).toLocaleDateString('fr-FR')}</strong>.</>}
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Note interne</label>
              <textarea rows={2} style={{ ...S.input, resize: 'vertical' }} value={form.note_interne ?? ''}
                placeholder="Réserves, points à confirmer, contexte. Ne fonde aucun taux."
                onChange={e => setForm({ ...form, note_interne: e.target.value })} />
              <div style={S.aide}>
                Ce qui reste à vérifier vit ici. Ce champ ne date rien et ne fonde rien :
                il n&apos;est jamais lu comme une justification.
              </div>
            </div>

            {/* ⚠️ LA CASE EST CONDITIONNÉE AU RÉFÉRENTIEL, ET LE REFUS EST NOMMÉ.
                Jamais un grisé muet : un contrôle qui empêche sans dire pourquoi
                oblige à deviner. La liste des manques vient du serveur, calculée
                par la même fonction que celle qui REFUSE l'écriture. */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6, fontSize: 13,
              opacity: (referentielIncomplet && !form.actif) ? 0.55 : 1 }}>
              <input type="checkbox" checked={!!form.actif}
                disabled={referentielIncomplet && !form.actif}
                onChange={e => setForm({ ...form, actif: e.target.checked })} />
              <span>Pays proposé à la création d&apos;un client</span>
            </label>

            {referentielIncomplet && (
              <div style={{ background: form.actif ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
                color: form.actif ? 'var(--gss-danger)' : '#92400E',
                padding: '10px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12, lineHeight: 1.6 }}>
                {form.actif ? (
                  <>
                    <strong>Anomalie : ce pays est proposé à la création alors que son référentiel est incomplet.</strong>
                    <br />
                    Il n&apos;a pas été décoché automatiquement — retirer un pays de l&apos;offre est une décision
                    commerciale, pas un effet de bord. Le moteur de paie refusera d&apos;émettre pour un client de ce pays.
                  </>
                ) : (
                  <><strong>Référentiel incomplet : ce pays ne peut pas être proposé à la création.</strong></>
                )}
                <div style={{ marginTop: 6 }}>
                  Il manque {manques.length} élément{manques.length > 1 ? 's' : ''} :
                  <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                    {manques.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
              * LA RÉSERVE DE COUVERTURE — UN PAYS COMPLET PEUT NE PAS PORTER
              * TOUS SES EMPLOIS (lot C, 19/09/2026)
              * ══════════════════════════════════════════════════════════════
              * ⚠️ CE BLOC N EST PAS UN MANQUE, ET IL NE DOIT PAS EN AVOIR L AIR.
              *    Le pays reste COMPLET et ouvrable — c est ce qui protège FOX,
              *    seul tenant en production. D où l orange, jamais le rouge.
              * ⓘ La PHRASE vient du serveur, calculée une seule fois : la liste
              *   des codes ne dit rien à personne, c est la cause qui ouvre une
              *   action (règle 1). Le détail se déplie pour qui veut les codes.
              */}
            {couverture && couverture.etat === 'partielle' && (
              <div style={{ background: 'rgba(245,158,11,0.12)', color: '#92400E',
                padding: '10px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12, lineHeight: 1.6 }}>
                <strong>Réserve — ce pays ne couvre pas toute sa propre grille.</strong>
                <div style={{ marginTop: 6 }}>{couverture.message}</div>
                <div style={{ marginTop: 6, opacity: 0.85 }}>
                  Le pays reste complet et ouvrable : un employeur peut y créer des postes et payer ses
                  agents dans les catégories couvertes. Mais aucun poste ne peut être créé pour les
                  emplois non couverts, et aucune classification ne doit leur être attribuée par
                  approximation — elle commanderait un salaire plancher faux.
                </div>
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: 'pointer' }}>
                    Voir les {couverture.crans_inatteignables.length} crans hors d&apos;atteinte
                  </summary>
                  <ul style={{ margin: '6px 0 0 18px', padding: 0, columns: 2 }}>
                    {couverture.crans_inatteignables.map((c) => (
                      <li key={c.code}>{c.code} — {c.libelle}</li>
                    ))}
                  </ul>
                </details>
              </div>
            )}

            {sourceManquante && (
              <div style={{ background: 'rgba(239,68,68,0.12)', color: 'var(--gss-danger)', padding: '8px 10px',
                borderRadius: 6, fontSize: 12, marginBottom: 10 }}>
                Un taux de TVA est saisi : la source devient obligatoire.
              </div>
            )}
            {erreur && <div style={{ background: 'rgba(239,68,68,0.12)', color: 'var(--gss-danger)',
              padding: '8px 10px', borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{erreur}</div>}
            {message && <div style={{ background: 'rgba(34,197,94,0.12)', color: 'var(--gss-success)',
              padding: '8px 10px', borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{message}</div>}

            <button onClick={enregistrer} disabled={envoi || sourceManquante}
              style={{ padding: '9px 18px', borderRadius: 7, border: 'none', fontWeight: 700, fontSize: 13,
                cursor: (envoi || sourceManquante) ? 'not-allowed' : 'pointer',
                background: (envoi || sourceManquante) ? 'var(--gss-border)' : 'var(--gss-red)',
                color: (envoi || sourceManquante) ? 'var(--gss-text-light)' : '#FFF' }}>
              {envoi ? 'Enregistrement...' : 'Enregistrer'}
            </button>

            {/* ⚠️ HISTORIQUE ALIMENTE PAR TRIGGER : une modification faite en SQL
                direct y figure aussi, marquee « sql_direct ». */}
            <div style={{ marginTop: 20 }}>
              <h3 style={{ fontSize: 13, margin: '0 0 8px' }}>
                Historique des modifications
                <span style={{ color: 'var(--gss-text-light)', fontWeight: 400 }}> · {histo.length}</span>
              </h3>
              {histo.length === 0 && <p style={{ fontSize: 12, color: 'var(--gss-text-light)', margin: 0 }}>
                Aucune modification enregistree depuis la mise en place de l'historique.</p>}
              {histo.length > 0 && (
                <div style={{ maxHeight: 240, overflowY: 'auto', fontSize: 12 }}>
                  {histo.map((h, i) => (
                    <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid var(--gss-border)' }}>
                      <div><strong>{h.champ}</strong> :{' '}
                        <span style={{ color: 'var(--gss-text-light)' }}>{h.valeur_avant || '(vide)'}</span>
                        {' → '}<span>{h.valeur_apres || '(vide)'}</span></div>
                      <div style={{ color: 'var(--gss-text-light)', fontSize: 11 }}>
                        {new Date(h.modifie_le).toLocaleString('fr-FR')} · {h.modifie_par_email}
                        {h.modifie_par_type === 'sql_direct' && <strong style={{ color: 'var(--gss-warning)' }}> · modification SQL directe</strong>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
